import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useAdaptiveFaq } from "@/hooks/useAdaptiveFaq";
import { trackFaqVariantShown, trackFaqItemOpen } from "@/lib/uxTracking";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface FAQProps {
  t: (key: string) => string;
}

const FAQ_GLOBAL_ITEMS: { id: string; qKey: string; aKey: string }[] = [
  { id: "faq_global_1", qKey: "home.faqGlobal1Q", aKey: "home.faqGlobal1A" },
  { id: "faq_global_2", qKey: "home.faqGlobal2Q", aKey: "home.faqGlobal2A" },
  { id: "faq_global_3", qKey: "home.faqGlobal3Q", aKey: "home.faqGlobal3A" },
  { id: "faq_global_4", qKey: "home.faqGlobal4Q", aKey: "home.faqGlobal4A" },
  { id: "faq_global_5", qKey: "home.faqGlobal5Q", aKey: "home.faqGlobal5A" },
  { id: "faq_global_6", qKey: "home.faqGlobal6Q", aKey: "home.faqGlobal6A" },
];

export function FAQ({ t }: FAQProps) {
  const { faqVariant, isAdaptive } = useAdaptiveFaq();

  useEffect(() => {
    if (isAdaptive) {
      trackFaqVariantShown(faqVariant);
    }
  }, [isAdaptive, faqVariant]);

  return (
    <section id="section-faq" className="py-20 bg-card/30" data-testid="section-faq" data-ux-faq-variant={faqVariant}>
      <div className="max-w-3xl mx-auto px-6">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-center text-foreground mb-12"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="text-faq-title"
        >
          {t("home.faqTitle")}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <Accordion type="single" collapsible className="space-y-0 divide-y divide-border/50">
            {FAQ_GLOBAL_ITEMS.map((item, i) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                id={item.id}
                className="border-0 bg-transparent px-0"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className={cn(
                    "py-6 text-lg font-serif hover:no-underline hover:text-primary transition-colors [&[data-state=open]]:text-primary"
                  )}
                  data-testid={`faq-trigger-${i}`}
                  onClick={() => trackFaqItemOpen(item.id)}
                >
                  {t(item.qKey)}
                  <ChevronDown className="h-5 w-5 shrink-0 text-primary transition-transform duration-200 [[data-state=open]_&]:rotate-180" />
                </AccordionTrigger>
                <AccordionContent
                  className="text-muted-foreground leading-relaxed pb-6"
                  data-testid={`faq-content-${i}`}
                >
                  {t(item.aKey)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
